/**
 * The pose library, in the sheet's names (v0, plus v1's vault row, slide, and roll). A pose is a set of joint angles
 * in degrees in the body frame (facing right): torso lean is positive
 * forward, shoulders and hips are positive when the limb swings forward
 * (toward facing) from hanging straight down, elbows and knees are positive
 * when flexed. `hipY` is the hip height above the simulation's feet point.
 * Angles are authored here and adjustable live in the pose editor.
 */

export interface Pose {
  hipY: number;
  torso: number;
  head: number;
  shoulderL: number;
  elbowL: number;
  shoulderR: number;
  elbowR: number;
  hipL: number;
  kneeL: number;
  hipR: number;
  kneeR: number;
}

export const POSE_KEYS = [
  "hipY",
  "torso",
  "head",
  "shoulderL",
  "elbowL",
  "shoulderR",
  "elbowR",
  "hipL",
  "kneeL",
  "hipR",
  "kneeR",
] as const satisfies readonly (keyof Pose)[];

export type PoseName =
  | "idle"
  | "run"
  | "accelerate"
  | "brake"
  | "crouch"
  | "jump"
  | "airExtend"
  | "airTuck"
  | "prepareLache"
  | "reach"
  | "catch"
  | "swingForward"
  | "swingBack"
  | "deadHang"
  | "release"
  | "flyToNext"
  | "landHigh"
  | "absorb"
  | "stabilize"
  | "vaultApproach"
  | "vaultPlant"
  | "vaultClear"
  | "vaultLand"
  | "slide"
  | "roll"
  | "ledgeHang"
  | "mantle";

export const POSE_NAMES: PoseName[] = [
  "idle",
  "run",
  "accelerate",
  "brake",
  "crouch",
  "jump",
  "airExtend",
  "airTuck",
  "prepareLache",
  "reach",
  "catch",
  "swingForward",
  "swingBack",
  "deadHang",
  "release",
  "flyToNext",
  "landHigh",
  "absorb",
  "stabilize",
  "vaultApproach",
  "vaultPlant",
  "vaultClear",
  "vaultLand",
  "slide",
  "roll",
  "ledgeHang",
  "mantle",
];

export const defaultPoses: Record<PoseName, Pose> = {
  idle: {
    hipY: 0.95,
    torso: 3,
    head: -3,
    shoulderL: -6,
    elbowL: 12,
    shoulderR: 6,
    elbowR: 12,
    hipL: 4,
    kneeL: 6,
    hipR: -4,
    kneeR: 6,
  },
  // Run: the legs come from the gait solver; the arms are driven by the phase on top of this base.
  run: {
    hipY: 0.86,
    torso: 14,
    head: -6,
    shoulderL: 0,
    elbowL: 85,
    shoulderR: 0,
    elbowR: 85,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  accelerate: {
    hipY: 0.82,
    torso: 28,
    head: -8,
    shoulderL: -35,
    elbowL: 95,
    shoulderR: 45,
    elbowR: 95,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  brake: {
    hipY: 0.84,
    torso: -16,
    head: 8,
    shoulderL: 35,
    elbowL: 30,
    shoulderR: 25,
    elbowR: 40,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  crouch: {
    hipY: 0.52,
    torso: 30,
    head: -12,
    shoulderL: 15,
    elbowL: 70,
    shoulderR: 25,
    elbowR: 70,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  // Takeoff: the lead arm drives up, the other stays low, so the blend into the air pose that
  // follows is a small move rather than both arms whipping over the head and back down.
  jump: {
    hipY: 1.0,
    torso: 6,
    head: -4,
    shoulderL: 20,
    elbowL: 40,
    shoulderR: 130,
    elbowR: 15,
    hipL: 8,
    kneeL: 6,
    hipR: -6,
    kneeR: 10,
  },
  // In the air: one arm reaching up and forward, the other trailing, legs split; not both arms
  // straight ahead, which reads as a sleepwalker.
  airExtend: {
    hipY: 0.95,
    torso: 4,
    head: -2,
    shoulderL: -30,
    elbowL: 45,
    shoulderR: 110,
    elbowR: 20,
    hipL: 30,
    kneeL: 35,
    hipR: -18,
    kneeR: 45,
  },
  airTuck: {
    hipY: 0.42,
    torso: 28,
    head: -10,
    shoulderL: 40,
    elbowL: 110,
    shoulderR: 50,
    elbowR: 110,
    hipL: 115,
    kneeL: 135,
    hipR: 105,
    kneeR: 130,
  },
  prepareLache: {
    hipY: 0.95,
    torso: -4,
    head: 4,
    shoulderL: 150,
    elbowL: 14,
    shoulderR: 160,
    elbowR: 10,
    hipL: 14,
    kneeL: 35,
    hipR: 4,
    kneeR: 45,
  },
  reach: {
    hipY: 0.95,
    torso: -2,
    head: 6,
    shoulderL: 168,
    elbowL: 4,
    shoulderR: 172,
    elbowR: 4,
    hipL: 18,
    kneeL: 30,
    hipR: 6,
    kneeR: 40,
  },
  catch: {
    hipY: 0.92,
    torso: -8,
    head: 8,
    shoulderL: 172,
    elbowL: 6,
    shoulderR: 176,
    elbowR: 6,
    hipL: -14,
    kneeL: 24,
    hipR: -22,
    kneeR: 30,
  },
  swingForward: {
    hipY: 0.9,
    torso: 6,
    head: 2,
    shoulderL: 176,
    elbowL: 4,
    shoulderR: 178,
    elbowR: 4,
    hipL: 44,
    kneeL: 12,
    hipR: 36,
    kneeR: 16,
  },
  swingBack: {
    hipY: 0.9,
    torso: -12,
    head: 10,
    shoulderL: 174,
    elbowL: 4,
    shoulderR: 178,
    elbowR: 4,
    hipL: -26,
    kneeL: 34,
    hipR: -32,
    kneeR: 40,
  },
  deadHang: {
    hipY: 0.9,
    torso: 0,
    head: 4,
    shoulderL: 176,
    elbowL: 3,
    shoulderR: 178,
    elbowR: 3,
    hipL: 2,
    kneeL: 6,
    hipR: -2,
    kneeR: 8,
  },
  release: {
    hipY: 0.94,
    torso: 10,
    head: 0,
    shoulderL: 110,
    elbowL: 25,
    shoulderR: 125,
    elbowR: 20,
    hipL: 22,
    kneeL: 20,
    hipR: 10,
    kneeR: 28,
  },
  flyToNext: {
    hipY: 0.95,
    torso: 2,
    head: 4,
    shoulderL: 135,
    elbowL: 12,
    shoulderR: 150,
    elbowR: 8,
    hipL: 12,
    kneeL: 30,
    hipR: 0,
    kneeR: 40,
  },
  landHigh: {
    hipY: 0.95,
    torso: 8,
    head: -4,
    shoulderL: 25,
    elbowL: 25,
    shoulderR: 50,
    elbowR: 25,
    hipL: 34,
    kneeL: 20,
    hipR: 26,
    kneeR: 26,
  },
  absorb: {
    hipY: 0.68,
    torso: 34,
    head: -12,
    shoulderL: 50,
    elbowL: 40,
    shoulderR: 65,
    elbowR: 40,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  stabilize: {
    hipY: 0.9,
    torso: 10,
    head: -4,
    shoulderL: 15,
    elbowL: 25,
    shoulderR: 25,
    elbowR: 25,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  // v1, authored from the sheet's vault and ground rows (the owner reviews them in the pose editor).
  // Approach: the tell for the vault window, arms reaching down and forward to the box, a slight crouch.
  vaultApproach: {
    hipY: 0.86,
    torso: 30,
    head: -10,
    shoulderL: 45,
    elbowL: 20,
    shoulderR: 60,
    elbowR: 15,
    hipL: 0,
    kneeL: 0,
    hipR: 0,
    kneeR: 0,
  },
  // Plant: torso pitched over straight arms on the box top; the legs swing up and forward, level with
  // the hips, so they pass over the box rather than hang into it.
  vaultPlant: {
    hipY: 0.5,
    torso: 45,
    head: -20,
    shoulderL: 40,
    elbowL: 5,
    shoulderR: 50,
    elbowR: 5,
    hipL: 100,
    kneeL: 12,
    hipR: 92,
    kneeR: 6,
  },
  // Clear: over the far half the torso comes up, the legs stay level and reach for the far side, and
  // the hands leave the box behind.
  vaultClear: {
    hipY: 0.5,
    torso: 15,
    head: -6,
    shoulderL: -15,
    elbowL: 20,
    shoulderR: -35,
    elbowR: 15,
    hipL: 96,
    kneeL: 10,
    hipR: 88,
    kneeR: 6,
  },
  // Land after a vault: arms out forward for balance, legs reaching down.
  vaultLand: {
    hipY: 0.82,
    torso: 10,
    head: -4,
    shoulderL: 30,
    elbowL: 25,
    shoulderR: 10,
    elbowR: 30,
    hipL: 40,
    kneeL: 35,
    hipR: 10,
    kneeR: 20,
  },
  // Slide: low and long, leaning back, the lead leg out along the ground, the trailing hand back low.
  slide: {
    hipY: 0.2,
    torso: -35,
    head: 25,
    shoulderL: -40,
    elbowL: 10,
    shoulderR: 45,
    elbowR: 40,
    hipL: 80,
    kneeL: 10,
    hipR: 20,
    kneeR: 120,
  },
  // Roll: curled tight; the turn itself comes from the attitude, not from the pose.
  roll: {
    hipY: 0.42,
    torso: 40,
    head: -35,
    shoulderL: 60,
    elbowL: 100,
    shoulderR: 70,
    elbowR: 100,
    hipL: 125,
    kneeL: 140,
    hipR: 115,
    kneeR: 135,
  },
  // Ledge hang (v3, the sheet's wall movement row): straight arms up and a little forward to the edge
  // (the hands are pinned to it by IK), the head up, the knees drawn up with the feet on the wall.
  ledgeHang: {
    hipY: 0.9,
    torso: -4,
    head: 14,
    shoulderL: 148,
    elbowL: 6,
    shoulderR: 152,
    elbowR: 6,
    hipL: 48,
    kneeL: 92,
    hipR: 34,
    kneeR: 80,
  },
  // Mantle (the sheet's climb up and pull over): the torso pitched over the edge, the arms pressing
  // down on it with bent elbows, the lead knee up onto the top, the other leg trailing.
  mantle: {
    hipY: 0.6,
    torso: 36,
    head: -12,
    shoulderL: 62,
    elbowL: 64,
    shoulderR: 76,
    elbowR: 58,
    hipL: 112,
    kneeL: 112,
    hipR: 28,
    kneeR: 70,
  },
};

export function clonePoses(p: Record<PoseName, Pose>): Record<PoseName, Pose> {
  const out = {} as Record<PoseName, Pose>;
  for (const name of POSE_NAMES) out[name] = { ...p[name] };
  return out;
}

export function mergePoses(base: Record<PoseName, Pose>, partial: unknown): Record<PoseName, Pose> {
  const out = clonePoses(base);
  if (!partial || typeof partial !== "object") return out;
  const rec = partial as Record<string, Record<string, unknown>>;
  for (const name of POSE_NAMES) {
    const p = rec[name];
    if (!p || typeof p !== "object") continue;
    for (const k of POSE_KEYS) {
      const v = p[k];
      if (typeof v === "number" && Number.isFinite(v)) out[name][k] = v;
    }
  }
  return out;
}
